/**
 * Paints the class diagram on a Canvas 2D, in UML's usual look:
 *
 * - a node is a box: «stereotype» and name on top (italic for an abstract class), then a
 *   compartment with the language and the description; its first category's colour tints the
 *   header and draws the border;
 * - a group is a package: a frame with its name in a tab;
 * - extends: solid line, hollow triangle; implements: dashed line, hollow triangle;
 *   uses: solid line, open arrowhead; depends on: dashed line, open arrowhead.
 *
 * Links from one package to another are bundled into one thick arrow with their count, opened
 * into their arrows when looked at (`state/diagram/bundles.ts`).
 *
 * A box's pins are rows at its bottom. The blueprint (`look.blueprint`) draws the same boxes
 * the Unreal Engine way: an input on each box's left, its pins on its right (filled when wired),
 * a "+" in the header of the box looked at, and every link as a curved wire from its pin (or
 * the header) to the input of where it leads (`state/diagram/pins.ts`); no bundles.
 *
 * Only what is on screen is drawn; text is left out when it would be too small to read. Boxes
 * and arrows too small on screen for their details (a big project zoomed out: tens of thousands)
 * are drawn as plain rectangles and lines, all those of one colour at once; zoomed out on many
 * bundles, only the counts of the longer ones are drawn (`countShown`). With a
 * box hovered or selected, its arrows stand out and the others fade, so a busy diagram stays
 * readable.
 */
import type { Link } from "@protocol/Link";
import type { Kinds } from "@protocol/Kinds";
import { matchesQuery } from "../../../shared/analysis";
import { linkKindOf } from "../../../shared/kinds";
import type { Pin } from "@protocol/Pin";
import {
  BOX_PAD,
  PACKAGE_TAB,
  PICTURE_H,
  PIN_LINE,
  curve,
  inputPoint,
  pinPoint,
  plusPoint,
  shownArrows,
  tabWidth,
  wiresOf,
  type BoxFonts,
  type DiagramBox,
  type DiagramBundle,
  type DiagramEdge,
  type DiagramFocus,
  type DiagramLayout,
  type Wire,
  countedBundles,
} from "../../../state/diagram";
import type { Palette } from "../palette";
import { sameEdge } from "../../../client/graph";

/** The palette plus the panel colours boxes are filled with. */
export interface DiagramPalette extends Palette {
  /** Box fill. */
  panel: string;
  /** Package tint and quiet lines. */
  line: string;
}

/** Reads the diagram's colours from the page's theme. */
export function diagramColours(palette: Palette): DiagramPalette {
  const css = getComputedStyle(document.documentElement);
  return { ...palette, panel: css.getPropertyValue("--panel").trim() || palette.canvas, line: css.getPropertyValue("--line").trim() || palette.muted };
}

/** What the picture depends on besides the layout. */
export interface DiagramLook {
  palette: DiagramPalette;
  fonts: BoxFonts;
  /** The graph's categories, in order (a node's first one picks its colour). */
  categories: readonly string[];
  /** Every kind (its arrows' lines, heads and colours). */
  kinds?: Kinds;
  /** Pan and zoom. */
  view: { x: number; y: number; k: number };
  /** Size of the area, CSS pixels. */
  width: number;
  /** Size of the area, CSS pixels. */
  height: number;
  selectedNode: string | null;
  /** Every selected box (one or several: the box selection). */
  selectedNodes: readonly string[];
  selectedLink: Link | null;
  selectedGroup: string | null;
  hoverNode: string | null;
  hoverLink: Link | null;
  hoverGroup: string | null;
  /** The selected bundle's key. */
  selectedBundle: string | null;
  /** The hovered bundle's key. */
  hoverBundle: string | null;
  /** What the user looks at: decides which bundles open. */
  focus: DiagramFocus;
  /** The search: boxes not matching it are faded. */
  query: string;
  /** Whether the link handle is drawn on the hovered or selected box (the user may edit). */
  handles: boolean;
  /** A link being drawn: from a box (from one of its pins in the blueprint) to a point (world units). */
  linking: { from: DiagramBox; x: number; y: number; pin?: string } | null;
  /** Drawn as a blueprint: inputs, pins and wires. */
  blueprint: boolean;
  /** The pins holding a wire, as `node id + "\u0000" + pin id`. */
  wired: ReadonlySet<string>;
  /** The words of a pin's row (its label, else where it leads, else its id). */
  pinLabel(node: string, pin: Pin): string;
  /** The pin under the pointer. */
  hoverPin: { node: string; pin: string } | null;
  /** The box selection's rectangle being drawn (world units). */
  band: { x1: number; y1: number; x2: number; y2: number } | null;
  /** The picture of a project's file, once loaded (`null`: not yet, or none). */
  picture?: (path: string) => CanvasImageSource & { width: number; height: number } | null;
}

/** Radius of the link handle, in pixels. */
export const HANDLE_RADIUS = 6;

/** Where a box's link handle is (world units): just outside its right border, half-way down. */
export function handleOf(box: DiagramBox, k: number): { x: number; y: number } {
  return { x: box.x + box.w + 10 / k, y: box.y + box.h / 2 };
}

/** Whether two links are the same one. */
export const sameLink = (a: Link | null, b: Link | null) => !!a && !!b && sameEdge(a, b);

/** A node's colour: its first category's place in the graph's order. */
function colourOf(box: DiagramBox, look: DiagramLook): string {
  const first = box.node.categories[0];
  if (first === undefined) return look.palette.noCategory;
  const at = look.categories.findIndex((c) => c.toLowerCase() === first.toLowerCase());
  return at < 0 ? look.palette.noCategory : look.palette.categories[at % look.palette.categories.length]!;
}

/** The colour of link kind `kind` (one of `kinds`), by its tone. */
function kindColour(kinds: Kinds | undefined, kind: string, p: DiagramPalette): string {
  const tone = linkKindOf(kinds, { kind }).tone;
  return tone === "rule" ? p.depend : tone === "plain" ? p.use : p.inherit;
}

/** A link kind's look and colour, as one paint uses them. */
interface KindPaint {
  /** Its view (line, head). */
  view: ReturnType<typeof linkKindOf>;
  /** Its colour. */
  colour: string;
}

/** Each paint's link kinds, worked out once per kind (a big project has tens of thousands of arrows). */
const kindPaints = new WeakMap<DiagramLook, Map<string, KindPaint>>();

/** The look of `link`'s kind in the paint of `look`. */
function kindPaint(look: DiagramLook, link: Pick<Link, "kind">): KindPaint {
  let paints = kindPaints.get(look);
  if (!paints) kindPaints.set(look, (paints = new Map()));
  let paint = paints.get(link.kind);
  if (!paint) {
    const view = linkKindOf(look.kinds, link);
    paints.set(link.kind, (paint = { view, colour: kindColour(look.kinds, view.id, look.palette) }));
  }
  return paint;
}

/** Below this width on screen (pixels), a box is a plain rectangle: nothing in it could be seen. */
const BOX_DETAIL_PX = 16;

/** Below this length on screen (pixels), an arrow has no head and is drawn with the others of its look at once. */
const HEAD_MIN_PX = 14;

/** Shapes drawn together: one path per look (`key`), stroked or filled once. */
class Batches {
  /** The paths' ends, by look. */
  private readonly paths = new Map<string, { style: string; alpha: number; dash: number[]; width: number; cap: CanvasLineCap; points: number[] }>();

  /** Adds a line from `(x1, y1)` to `(x2, y2)` to the batch of its look. */
  line(style: string, alpha: number, dash: number[], width: number, x1: number, y1: number, x2: number, y2: number, cap: CanvasLineCap = "butt"): void {
    const key = `${style}|${alpha}|${dash.join(",")}|${width}|${cap}`;
    let path = this.paths.get(key);
    if (!path) this.paths.set(key, (path = { style, alpha, dash, width, cap, points: [] }));
    path.points.push(x1, y1, x2, y2);
  }

  /** Strokes every batch's lines. */
  strokeLines(ctx: CanvasRenderingContext2D): void {
    for (const path of this.paths.values()) {
      ctx.globalAlpha = path.alpha;
      ctx.strokeStyle = path.style;
      ctx.lineWidth = path.width;
      ctx.lineCap = path.cap;
      ctx.setLineDash(path.dash);
      ctx.beginPath();
      const q = path.points;
      for (let i = 0; i < q.length; i += 4) {
        ctx.moveTo(q[i]!, q[i + 1]!);
        ctx.lineTo(q[i + 2]!, q[i + 3]!);
      }
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.lineCap = "butt";
    ctx.globalAlpha = 1;
    this.paths.clear();
  }

  /** Adds a rectangle to the batch of its look. */
  rect(style: string, alpha: number, x: number, y: number, w: number, h: number): void {
    this.line(style, alpha, [], 0, x, y, w, h);
  }

  /** Fills every batch's rectangles in the panel colour, then tints and outlines them in their own. */
  fillRects(ctx: CanvasRenderingContext2D, panel: string, px: number): void {
    for (const path of this.paths.values()) {
      const q = path.points;
      ctx.beginPath();
      for (let i = 0; i < q.length; i += 4) ctx.rect(q[i]!, q[i + 1]!, q[i + 2]!, q[i + 3]!);
      ctx.globalAlpha = path.alpha;
      ctx.fillStyle = panel;
      ctx.fill();
      ctx.globalAlpha = path.alpha * 0.35;
      ctx.fillStyle = path.style;
      ctx.fill();
      ctx.globalAlpha = path.alpha;
      ctx.strokeStyle = path.style;
      ctx.lineWidth = 1.4 * px;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    this.paths.clear();
  }
}

/** Paints the whole diagram. `ctx` is already scaled to CSS pixels. Gives how many pictures it painted. */
export function paintDiagram(ctx: CanvasRenderingContext2D, layout: DiagramLayout, look: DiagramLook): number {
  const { view: v, palette: p } = look;
  ctx.save();
  ctx.fillStyle = p.canvas;
  ctx.fillRect(0, 0, look.width, look.height);
  ctx.translate(v.x, v.y);
  ctx.scale(v.k, v.k);
  // What is on screen, in world units (with a margin for strokes).
  const margin = 20 / v.k;
  const seen = { x1: -v.x / v.k - margin, y1: -v.y / v.k - margin, x2: (look.width - v.x) / v.k + margin, y2: (look.height - v.y) / v.k + margin };
  const onScreen = (r: { x: number; y: number; w: number; h: number }) => r.x < seen.x2 && r.x + r.w > seen.x1 && r.y < seen.y2 && r.y + r.h > seen.y1;
  const readable = v.k * 12 >= 5;
  const px = 1 / v.k;

  for (const pack of layout.packages) {
    if (!onScreen(pack)) continue;
    const selected = pack.id === look.selectedGroup;
    const hovered = pack.id === look.hoverGroup;
    const tab = tabWidth(pack);
    ctx.beginPath();
    ctx.moveTo(pack.x, pack.y + PACKAGE_TAB);
    ctx.lineTo(pack.x, pack.y);
    ctx.lineTo(pack.x + tab, pack.y);
    ctx.lineTo(pack.x + tab, pack.y + PACKAGE_TAB);
    ctx.lineTo(pack.x + pack.w, pack.y + PACKAGE_TAB);
    ctx.lineTo(pack.x + pack.w, pack.y + pack.h);
    ctx.lineTo(pack.x, pack.y + pack.h);
    ctx.closePath();
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = p.line;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = selected ? p.accent : hovered ? p.fg : p.muted;
    ctx.lineWidth = (selected ? 2.2 : 1.2) * px;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pack.x, pack.y + PACKAGE_TAB);
    ctx.lineTo(pack.x + tab, pack.y + PACKAGE_TAB);
    ctx.stroke();
    if (readable) {
      ctx.fillStyle = selected ? p.accent : p.fg;
      ctx.font = look.fonts.name;
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      ctx.fillText(pack.name, pack.x + 8, pack.y + PACKAGE_TAB / 2 + 1, tab - 14);
      // A folded package says what it holds, in its closed body.
      if (pack.folded !== undefined) {
        ctx.fillStyle = p.muted;
        ctx.textAlign = "center";
        ctx.fillText(`${pack.folded} ${pack.folded === 1 ? "box" : "boxes"} · folded`, pack.x + pack.w / 2, pack.y + PACKAGE_TAB + (pack.h - PACKAGE_TAB) / 2, pack.w - 16);
      }
    }
  }

  // The box looked at: its arrows stand out, the others fade.
  const focus = look.linking?.from.id ?? look.hoverNode ?? look.selectedNode;
  const shown = shownArrows(layout, look.focus);
  if (look.blueprint) {
    for (const wire of wiresOf(layout)) {
      const l = wire.edge.link;
      const box = { x: Math.min(wire.x1, wire.x2, wire.c1x, wire.c2x), y: Math.min(wire.y1, wire.y2), w: Math.abs(Math.max(wire.x1, wire.x2, wire.c1x, wire.c2x) - Math.min(wire.x1, wire.x2, wire.c1x, wire.c2x)), h: Math.abs(wire.y2 - wire.y1) };
      if (!onScreen(box)) continue;
      const touches = focus !== null && (l.from === focus || l.to === focus);
      const searched = !look.query || matchesQuery(layout.byId.get(l.from)!.node, look.query) || matchesQuery(layout.byId.get(l.to)!.node, look.query);
      const alpha = sameLink(l, look.selectedLink) || touches ? 1 : !searched || focus !== null || look.selectedLink ? 0.25 : 0.85;
      paintWire(ctx, wire, look, alpha, touches, px);
    }
  }
  const batches = new Batches();
  for (const edge of look.blueprint ? [] : shown.edges) {
    const box = { x: Math.min(edge.x1, edge.x2), y: Math.min(edge.y1, edge.y2), w: Math.abs(edge.x2 - edge.x1), h: Math.abs(edge.y2 - edge.y1) };
    if (!onScreen(box)) continue;
    const l = edge.link;
    const touches = focus !== null && (l.from === focus || l.to === focus);
    const searched = !look.query || matchesQuery(layout.byId.get(l.from)!.node, look.query) || matchesQuery(layout.byId.get(l.to)!.node, look.query);
    const alpha = sameLink(l, look.selectedLink) || touches ? 1 : !searched || focus !== null || look.selectedLink ? 0.14 : 0.7;
    if (!touches && Math.hypot(edge.x2 - edge.x1, edge.y2 - edge.y1) * v.k < HEAD_MIN_PX && !sameLink(l, look.selectedLink) && !sameLink(l, look.hoverLink)) {
      const kind = kindPaint(look, l);
      batches.line(kind.colour, alpha, dashOf(kind.view.line, px), 1.4 * px, edge.x1, edge.y1, edge.x2, edge.y2);
    } else paintEdge(ctx, edge, look, alpha, touches, px);
  }
  batches.strokeLines(ctx);
  /** A bundle's extent, to skip those off screen. */
  const extent = (b: DiagramBundle) => ({ x: Math.min(b.x1, b.x2), y: Math.min(b.y1, b.y2), w: Math.abs(b.x2 - b.x1), h: Math.abs(b.y2 - b.y1) });
  const bundles = shown.bundles.filter((b) => onScreen(extent(b)));
  const drawnCounts = countedBundles(shown.bundles, v.k);
  const counted = drawnCounts ? bundles.filter((b) => drawnCounts.has(b)) : bundles;
  for (const bundle of bundles) paintBundle(bundle, look, focus !== null || !!look.selectedLink ? 0.35 : 0.85, px, batches);
  batches.strokeLines(ctx);

  const q = look.query;
  let pictures = 0;
  // Boxes too small to show anything inside: plain rectangles, those of one colour at once, under the others.
  const detailed: DiagramBox[] = [];
  const chosen = new Set(look.selectedNodes);
  for (const box of layout.boxes) {
    if (!onScreen(box)) continue;
    const plain = box.w * v.k < BOX_DETAIL_PX && !box.picture && !look.blueprint && box.id !== look.selectedNode && box.id !== look.hoverNode && !chosen.has(box.id);
    if (plain) batches.rect(colourOf(box, look), q && !matchesQuery(box.node, q) ? 0.25 : 1, box.x, box.y, box.w, box.h);
    else detailed.push(box);
  }
  batches.fillRects(ctx, p.panel, px);
  for (const box of detailed) {
    ctx.globalAlpha = q && !matchesQuery(box.node, q) ? 0.25 : 1;
    if (paintBox(ctx, box, look, readable, px)) pictures++;
  }
  ctx.globalAlpha = 1;
  // The counts go above the boxes: they are what is clicked to open a bundle.
  for (const bundle of counted) paintCount(ctx, bundle, look, px);

  if (look.handles && look.blueprint) {
    // The "+" of the box looked at: a new pin.
    const id = look.linking?.from.id ?? look.hoverNode ?? look.selectedNode;
    const box = id ? layout.byId.get(id) : undefined;
    if (box && !look.linking) {
      const c = plusPoint(box);
      ctx.beginPath();
      ctx.arc(c.x, c.y, 7 * Math.max(px, 0.6), 0, Math.PI * 2);
      ctx.fillStyle = p.accent;
      ctx.fill();
      ctx.strokeStyle = p.panel;
      ctx.lineWidth = 1.6 * Math.max(px, 0.6);
      const r = 3.6 * Math.max(px, 0.6);
      ctx.beginPath();
      ctx.moveTo(c.x - r, c.y);
      ctx.lineTo(c.x + r, c.y);
      ctx.moveTo(c.x, c.y - r);
      ctx.lineTo(c.x, c.y + r);
      ctx.stroke();
    }
  }
  if (look.handles && !look.blueprint) {
    const id = look.linking?.from.id ?? look.hoverNode ?? look.selectedNode;
    const box = id ? layout.byId.get(id) : undefined;
    if (box) {
      const h = handleOf(box, v.k);
      ctx.beginPath();
      ctx.arc(h.x, h.y, HANDLE_RADIUS * px, 0, Math.PI * 2);
      ctx.fillStyle = p.accent;
      ctx.fill();
    }
  }
  if (look.linking) {
    const { from, x, y } = look.linking;
    ctx.setLineDash([6 * px, 4 * px]);
    ctx.strokeStyle = p.accent;
    ctx.lineWidth = 1.8 * px;
    ctx.beginPath();
    const index = look.linking.pin ? from.pins.findIndex((pin) => pin.id === look.linking!.pin) : -1;
    if (look.blueprint && index >= 0) {
      const start = pinPoint(from, index);
      const w = curve(start.x, start.y, x, y);
      ctx.moveTo(w.x1, w.y1);
      ctx.bezierCurveTo(w.c1x, w.c1y, w.c2x, w.c2y, w.x2, w.y2);
    } else {
      ctx.moveTo(from.x + from.w / 2, from.y + from.h / 2);
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (look.band) {
    const { x1, y1, x2, y2 } = look.band;
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = p.accent;
    ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    ctx.globalAlpha = 1;
    ctx.setLineDash([5 * px, 3 * px]);
    ctx.strokeStyle = p.accent;
    ctx.lineWidth = 1.2 * px;
    ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    ctx.setLineDash([]);
  }
  ctx.restore();
  return pictures;
}

/** One box: header (stereotype, name), compartment (language, description), border. */
/**
 * One box. Zoomed out, its text is left out but its picture stays, as on a whiteboard (Miro):
 * pictures are what is recognised from afar. Gives whether a picture was painted.
 */
function paintBox(ctx: CanvasRenderingContext2D, box: DiagramBox, look: DiagramLook, readable: boolean, px: number): boolean {
  const p = look.palette;
  const colour = colourOf(box, look);
  const selected = box.id === look.selectedNode || look.selectedNodes.includes(box.id);
  const hovered = box.id === look.hoverNode;
  ctx.fillStyle = p.panel;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  const alpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha * 0.2;
  ctx.fillStyle = colour;
  ctx.fillRect(box.x, box.y, box.w, box.header);
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.4 * px;
  if (box.details.length || box.pins.length || box.picture) {
    ctx.beginPath();
    ctx.moveTo(box.x, box.y + box.header);
    ctx.lineTo(box.x + box.w, box.y + box.header);
    ctx.stroke();
  }
  if (box.pins.length && (box.details.length || box.picture)) {
    ctx.beginPath();
    ctx.moveTo(box.x, box.y + box.pinsTop);
    ctx.lineTo(box.x + box.w, box.y + box.pinsTop);
    ctx.stroke();
  }
  if (selected || hovered) {
    ctx.strokeStyle = selected ? p.accent : p.fg;
    ctx.lineWidth = (selected ? 2.6 : 1.8) * px;
  }
  ctx.strokeRect(box.x, box.y, box.w, box.h);
  if (look.blueprint) paintPins(ctx, box, look, colour, px);
  const pictured = !!box.picture && paintPicture(ctx, box, look);
  if (!readable) return pictured;

  const cx = box.x + box.w / 2;
  let y = box.y + 10;
  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  if (box.stereotype) {
    ctx.fillStyle = p.muted;
    ctx.font = look.fonts.stereotype;
    ctx.fillText(box.stereotype, cx, y);
    y += 14;
  }
  ctx.fillStyle = p.fg;
  ctx.font = box.italic ? look.fonts.abstractName : look.fonts.name;
  ctx.fillText(box.name, cx, y + 1, box.w - 12);
  ctx.font = look.fonts.detail;
  ctx.textBaseline = "middle";
  ctx.textAlign = "right";
  box.pins.forEach((pin, i) => {
    const hovered = look.hoverPin?.node === box.id && look.hoverPin.pin === pin.id;
    ctx.fillStyle = hovered ? p.accent : look.wired.has(`${box.id}\u0000${pin.id}`) ? p.fg : p.muted;
    ctx.fillText(look.pinLabel(box.id, pin), box.x + box.w - 14, box.y + box.pinsTop + i * PIN_LINE + PIN_LINE / 2 + 1, box.w - 24);
  });
  ctx.textBaseline = "top";
  if (!box.details.length) return pictured;
  ctx.textAlign = "left";
  y = box.y + box.header + 5;
  box.details.forEach((line, i) => {
    ctx.fillStyle = i < box.values ? p.fg : p.muted;
    ctx.fillText(line, box.x + 10, y + 1, box.w - 16);
    y += 15;
  });
  return pictured;
}

/** The box's picture, fitted inside its place (or a frame while it loads). Gives whether it was there to paint. */
function paintPicture(ctx: CanvasRenderingContext2D, box: DiagramBox, look: DiagramLook): boolean {
  const [x, y, w, h] = [box.x + BOX_PAD, box.y + box.pictureTop, box.w - 2 * BOX_PAD, PICTURE_H];
  const image = box.picture ? look.picture?.(box.picture) : null;
  if (!image || !image.width || !image.height) {
    ctx.strokeStyle = look.palette.line;
    ctx.strokeRect(x, y, w, h);
    return false;
  }
  const scale = Math.min(w / image.width, h / image.height);
  const [iw, ih] = [image.width * scale, image.height * scale];
  ctx.drawImage(image, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
  return true;
}

/** A blueprint box's input (left) and pins (right): a pin is filled when it holds a wire. */
function paintPins(ctx: CanvasRenderingContext2D, box: DiagramBox, look: DiagramLook, colour: string, px: number): void {
  const p = look.palette;
  const r = 4.5 * Math.max(px, 0.5);
  const input = inputPoint(box);
  ctx.lineWidth = 1.5 * Math.max(px, 0.5);
  ctx.beginPath();
  ctx.moveTo(input.x - r * 0.4, input.y - r * 1.2);
  ctx.lineTo(input.x + r * 1.1, input.y);
  ctx.lineTo(input.x - r * 0.4, input.y + r * 1.2);
  ctx.closePath();
  ctx.fillStyle = p.panel;
  ctx.fill();
  ctx.strokeStyle = colour;
  ctx.stroke();
  box.pins.forEach((pin, i) => {
    const at = pinPoint(box, i);
    const hovered = look.hoverPin?.node === box.id && look.hoverPin.pin === pin.id;
    ctx.beginPath();
    ctx.arc(at.x, at.y, hovered ? r * 1.3 : r, 0, Math.PI * 2);
    ctx.fillStyle = look.wired.has(`${box.id}\u0000${pin.id}`) ? colour : p.panel;
    ctx.fill();
    ctx.strokeStyle = hovered ? p.accent : colour;
    ctx.stroke();
  });
}

/** A blueprint wire: a curve from its pin to the input it leads to, in its kind's colour and line. */
function paintWire(ctx: CanvasRenderingContext2D, wire: Wire, look: DiagramLook, alpha: number, focused: boolean, px: number): void {
  const kind = linkKindOf(look.kinds, wire.edge.link);
  const selected = sameLink(wire.edge.link, look.selectedLink);
  const hovered = sameLink(wire.edge.link, look.hoverLink);
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = selected ? look.palette.accent : kindColour(look.kinds, kind.id, look.palette);
  ctx.lineWidth = (selected ? 3.4 : hovered || focused ? 2.6 : 2) * px;
  ctx.setLineDash(kind.line === "dashed" ? [7 * px, 4 * px] : kind.line === "dotted" ? [1.5 * px, 3.5 * px] : []);
  ctx.beginPath();
  ctx.moveTo(wire.x1, wire.y1);
  ctx.bezierCurveTo(wire.c1x, wire.c1y, wire.c2x, wire.c2y, wire.x2, wire.y2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

/**
 * A bundle: one thick arrow, as wide as its links are many (its count is drawn above the boxes,
 * by {@link paintCount}). Added to `batches`: a big project has thousands.
 */
function paintBundle(bundle: DiagramBundle, look: DiagramLook, alpha: number, px: number, batches: Batches): void {
  const p = look.palette;
  const selected = bundle.key === look.selectedBundle;
  const hovered = bundle.key === look.hoverBundle;
  const colour = selected ? p.accent : bundle.kinds.length === 1 ? kindPaint(look, { kind: bundle.kinds[0]! }).colour : p.muted;
  // Widths in half pixels, so bundles of nearly the same size share a batch.
  const width = Math.round((Math.min(9, 2 + Math.log2(bundle.links.length) * 1.6) + (hovered ? 1 : 0)) * 2) / 2;
  const angle = Math.atan2(bundle.y2 - bundle.y1, bundle.x2 - bundle.x1);
  const size = (10 + width) * Math.min(1.4, Math.max(0.8, px * 1.2));
  const shown = selected || hovered ? 1 : alpha;
  const [hx, hy] = [bundle.x2 - Math.cos(angle) * size * 0.5, bundle.y2 - Math.sin(angle) * size * 0.5];
  batches.line(colour, shown, [], width * px, bundle.x1, bundle.y1, hx, hy, "round");
  const head = Math.max(1.6, width * 0.6) * px;
  batches.line(colour, shown, [], head, bundle.x2 - Math.cos(angle - 0.45) * size, bundle.y2 - Math.sin(angle - 0.45) * size, bundle.x2, bundle.y2, "round");
  batches.line(colour, shown, [], head, bundle.x2 - Math.cos(angle + 0.45) * size, bundle.y2 - Math.sin(angle + 0.45) * size, bundle.x2, bundle.y2, "round");
}

/** A bundle's count, in a badge the size of its hit area (`countRadius`), in the middle of its arrow. */
function paintCount(ctx: CanvasRenderingContext2D, bundle: DiagramBundle, look: DiagramLook, px: number): void {
  const p = look.palette;
  const selected = bundle.key === look.selectedBundle;
  const hovered = bundle.key === look.hoverBundle;
  const text = String(bundle.links.length);
  const r = (8 + text.length * 3) * px;
  const mx = (bundle.x1 + bundle.x2) / 2;
  const my = (bundle.y1 + bundle.y2) / 2;
  ctx.beginPath();
  ctx.arc(mx, my, r, 0, Math.PI * 2);
  ctx.fillStyle = p.panel;
  ctx.fill();
  ctx.strokeStyle = selected ? p.accent : hovered ? p.fg : bundle.kinds.length === 1 ? kindPaint(look, { kind: bundle.kinds[0]! }).colour : p.muted;
  ctx.lineWidth = (selected || hovered ? 2.2 : 1.4) * px;
  ctx.stroke();
  ctx.fillStyle = selected ? p.accent : p.fg;
  ctx.font = `600 ${11 * px}px ${look.fonts.detail.replace(/^[\d.]+px\s*/, "")}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, mx, my + 0.5 * px);
}

/** The dashes of a line style, at `px` world units per pixel. */
function dashOf(line: string, px: number): number[] {
  return line === "dashed" ? [7 * px, 4 * px] : line === "dotted" ? [1.5 * px, 3.5 * px] : [];
}

/** One arrow, in its kind's UML look (a custom kind's own line and head, else its base's). */
function paintEdge(ctx: CanvasRenderingContext2D, edge: DiagramEdge, look: DiagramLook, alpha: number, focused: boolean, px: number): void {
  const { view: kind, colour: own } = kindPaint(look, edge.link);
  const selected = sameLink(edge.link, look.selectedLink);
  const hovered = sameLink(edge.link, look.hoverLink);
  const colour = selected ? look.palette.accent : own;
  const closed = kind.head === "hollow" || kind.head === "filled";
  const angle = Math.atan2(edge.y2 - edge.y1, edge.x2 - edge.x1);
  const size = 12 * Math.min(1.4, Math.max(0.8, px * 1.2));
  // A closed head's line stops at its base, so the line does not show through it.
  const stop = closed ? size * 0.87 : kind.head === "diamond" ? size * 1.75 : 0;
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = colour;
  ctx.lineWidth = (selected ? 2.6 : hovered || focused ? 2 : 1.4) * px;
  ctx.setLineDash(dashOf(kind.line, px));
  ctx.beginPath();
  ctx.moveTo(edge.x1, edge.y1);
  ctx.lineTo(edge.x2 - Math.cos(angle) * stop, edge.y2 - Math.sin(angle) * stop);
  ctx.stroke();
  ctx.setLineDash([]);
  if (kind.head === "none") {
    ctx.globalAlpha = 1;
    return;
  }
  const left = { x: edge.x2 - Math.cos(angle - 0.5) * size, y: edge.y2 - Math.sin(angle - 0.5) * size };
  const right = { x: edge.x2 - Math.cos(angle + 0.5) * size, y: edge.y2 - Math.sin(angle + 0.5) * size };
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(edge.x2, edge.y2);
  ctx.lineTo(right.x, right.y);
  if (kind.head === "diamond") {
    ctx.lineTo(edge.x2 - Math.cos(angle) * stop, edge.y2 - Math.sin(angle) * stop);
    ctx.closePath();
    ctx.fillStyle = look.palette.canvas;
    ctx.fill();
  } else if (closed) {
    ctx.closePath();
    ctx.fillStyle = kind.head === "filled" ? colour : look.palette.canvas;
    ctx.fill();
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
}
